import { IonBadge, IonButton, IonButtons, IonIcon, IonMenuButton, IonMenuToggle, IonSearchbar, IonTitle, IonToolbar } from '@ionic/react';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';

import { options } from 'ionicons/icons';
import { logTransaction } from '../../../common/log';
import useDebounce from '../../../hooks/useDebounce';
import { LogType } from '../../../models/log.model';
import store from '../../../store';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { setRepairOrdersFromSyncByKeywordAsync } from '../../../store/repairOrderSlice';
import ProfileChip from '../../profileChip/ProfileChip';
import { IonHeaderWrapper } from './JobSearchHeader.styles';
import { RepairOrderOrderByField } from '../../../models/dms/repairOrder.model';
import moment from 'moment';

interface IComponentProps {
  handleSearch?: (keyword: string) => void;
  title?: string;
}

interface RefHandle {
  applyFilter: (extended: boolean) => void;
}

const JobSearchHeader: React.ForwardRefRenderFunction<RefHandle, IComponentProps> = function ({ handleSearch, title = 'Job Search' }, forwardedRef) {
  useImperativeHandle(forwardedRef, () => ({
    applyFilter: (extended) => applyFilter(keywordFilter, extended),
  }));

  const [keywordFilter, setKeywordFilter] = useState('');
  const { isDemo } = useAppSelector((state) => state.app);
  const { repairOrderSync } = useAppSelector((state) => state.repairOrderSync);
  const { filtersOrSorts } = useAppSelector((state) => state.filterMenu);
  const { technician } = store.getState().auth;

  const dispatch = useAppDispatch();

  useEffect(() => {
    const { techCodeCheckbox, selectedBranchCode, todaysBookingsCheckbox, orderByDateDescending } = store.getState().filterMenu;
    dispatch(
      setRepairOrdersFromSyncByKeywordAsync({
        keyword: keywordFilter,
        branchCode: selectedBranchCode,
        techCode: techCodeCheckbox ? technician?.code : '',
        startDateTime: todaysBookingsCheckbox ? moment().startOf('day') : undefined,
        endDateTime: todaysBookingsCheckbox ? moment().endOf('day') : undefined,
        orderBy: { field: RepairOrderOrderByField.dropOffDateTime, descending: orderByDateDescending },
      }),
    );

    // Demo
    if (isDemo) {
      setKeywordFilter('chris');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repairOrderSync?.timestamp]);

  const applyFilter = (keyword: string, extended?: boolean) => {
    logTransaction(LogType.Info, `Search for repair order by keyword '${keyword}'`);
    const { techCodeCheckbox, selectedBranchCode, todaysBookingsCheckbox, orderByDateDescending } = store.getState().filterMenu;
    dispatch(
      setRepairOrdersFromSyncByKeywordAsync({
        keyword,
        branchCode: selectedBranchCode,
        techCode: techCodeCheckbox ? technician?.code : '',
        startDateTime: todaysBookingsCheckbox ? moment().startOf('day') : undefined,
        endDateTime: todaysBookingsCheckbox ? moment().endOf('day') : undefined,
        extended,
        orderBy: { field: RepairOrderOrderByField.dropOffDateTime, descending: orderByDateDescending },
      }),
    );
    handleSearch && handleSearch(keyword);
  };

  const handleOnJobSearchbarChange = (e: any) => {
    setKeywordFilter(e.detail.value || '');
    searchJobs(e.detail.value || '');
  };

  const searchJobs = useDebounce(applyFilter);

  return (
    <IonHeaderWrapper color="lightest">
      <IonToolbar color="primary">
        <IonButtons slot="start">
          <IonMenuButton color="secondary" />
        </IonButtons>
        <IonTitle>{title}</IonTitle>
        <ProfileChip slot="end" />
      </IonToolbar>

      <IonToolbar color="primary">
        <IonSearchbar value={keywordFilter} debounce={0} animated placeholder="Search Jobs" onIonInput={(e) => handleOnJobSearchbarChange(e)}></IonSearchbar>
        <IonMenuToggle menu="jobsearch-filter" slot="end" autoHide={false}>
          <IonButton fill="clear" color="darkest" className="toggle-button">
            <IonIcon icon={options} slot="icon-only" />
            {filtersOrSorts >= 1 && <IonBadge color="tertiary">{filtersOrSorts}</IonBadge>}
          </IonButton>
        </IonMenuToggle>
      </IonToolbar>
    </IonHeaderWrapper>
  );
};

export default forwardRef(JobSearchHeader);
